import { format, formatDistanceToNow, isValid, parseISO } from "date-fns";

/**
 * Parses an ISO 8601 string defensively. Returns `null` if the string is
 * missing or unparseable so the caller can fall back to a placeholder
 * instead of rendering "Invalid Date".
 */
export function parseAuditDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
}

/**
 * Short relative form for the table column.
 *
 * Examples: "3 minutes ago", "2 hours ago", "5 days ago".
 * Returns the absolute date if older than ~30 days for legibility.
 */
export function formatAuditDateRelative(iso: string | null | undefined): string {
  const date = parseAuditDate(iso);
  if (!date) return "—";

  const ageMs = Date.now() - date.getTime();
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

  if (ageMs > THIRTY_DAYS_MS) {
    return format(date, "yyyy-MM-dd");
  }
  return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Long absolute form for tooltips and the detail view.
 *
 * Example: "2026-04-23 18:14:22 UTC".
 * Local timezone is used (no manual conversion to UTC) — `date-fns` honors
 * the user's browser locale, which matches what the rest of the dashboard
 * does for `createdAt` columns.
 */
export function formatAuditDateAbsolute(
  iso: string | null | undefined
): string {
  const date = parseAuditDate(iso);
  if (!date) return "—";
  return format(date, "yyyy-MM-dd HH:mm:ss");
}
